import {
  ChangeEvent,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import React from "react";

interface SelectionContextType<T> {
  selected: ReadonlyArray<number>;
  selectedLength: number;
  isSelected: (item: T) => boolean;
  handleItemChange: (item: T, checked: boolean) => void;
  isAllSelected: boolean;
  isAllIndeterminate: boolean;
  handleSelectAllChange: (
    event: ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => void;
}

export function createSelectionContext<T>() {
  console.log("rendering creating selection context");
  const Context = createContext<SelectionContextType<T> | null>(null);
  const useSelection = () => {
    const ctx = useContext(Context);
    if (!ctx)
      throw new Error("useSelection must be used within SelectionProvider");
    return ctx;
  };

  const SelectionProvider: React.FC<{
    items: Readonly<T[]>;
    getId: (item: T) => number;
    children: React.ReactNode;
  }> = ({ items, children, getId }) => {
    const [selectedIds, setSelectedIds] = useState<ReadonlyArray<number>>([]);

    const selectedKey = useMemo(
      () => [...selectedIds].sort().toString(),
      [selectedIds]
    );

    const itemsKey = useMemo(
      () =>
        items
          .map((i) => getId(i))
          .sort()
          .toString(),
      [items]
    );

    useEffect(() => {
      // When we get a new list of items, reset
      setSelectedIds([]);
    }, [itemsKey]);

    console.log("rendering selection provider", selectedKey, itemsKey);
    const isSelected = (item: T) => selectedIds.includes(getId(item));

    const handleItemChange = (item: T, checked: boolean) => {
      const id = getId(item);
      setSelectedIds((prev) =>
        checked ? [...prev, id] : prev.filter((i) => i !== id)
      );
    };

    const handleSelectAllChange = (
      _: ChangeEvent<HTMLInputElement>,
      checked: boolean
    ) => {
      setSelectedIds(checked ? items.map(getId) : []);
    };

    const isAllSelected =
      items.length > 0 && selectedIds.length === items.length;
    const isAllIndeterminate =
      selectedIds.length > 0 && selectedIds.length < items.length;
    const selectedLength = selectedIds.length;

    const value = useMemo(
      () => ({
        selected: selectedIds,
        selectedLength,
        isSelected,
        handleItemChange,
        isAllSelected,
        isAllIndeterminate,
        handleSelectAllChange,
      }),
      [selectedKey, itemsKey]
    );
    return <Context.Provider value={value}>{children}</Context.Provider>;
  };

  return { useSelection, SelectionProvider };
}
